import { useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import { Project } from '@types'

import CaseIcon from '@assets/images/case-icon.svg?react'

import { useProjects } from '@hooks/project/projects.hook'

import { Select, Option } from '@components/Select'

import { Container, SelectedItemContainer, SelectedIconContainer, SelectedItemLabel } from './SelectProject.styled'

export const SelectProject = (): JSX.Element => {
  const { data, loading, error } = useProjects()

  const isLoading = loading ? 1 : error ? -1 : 0

  const projects = useMemo(() => data?.projects || [], [data?.projects])
  const sharedProjects = useMemo(() => data?.sharedProjects || [], [data?.sharedProjects])

  const navigate = useNavigate()

  const { projectId } = useParams()
  const project = projects.find((p) => p.id == projectId) || sharedProjects.find((p) => p.id == projectId)

  const { t, i18n } = useTranslation()

  const { pathname } = useLocation()

  const getDefaultProjectPath = useCallback(
    (project: Project) => {
      const path = `/${i18n.language}/projects/${project.id}`
      return path
    },
    [i18n.language],
  )

  const getProjectPath = useCallback(
    (project: Project) => {
      if (pathname.startsWith(`/${i18n.language}/projects/`)) {
        const path = pathname
          .split('/')
          .reduce((acc, sub, index) => (index == 3 ? `${acc}/${project.id}` : `${acc}/${sub}`))

        return path
      }

      const path = `${getDefaultProjectPath(project)}/view`
      return path
    },
    [getDefaultProjectPath, i18n.language, pathname],
  )

  useEffect(() => {
    if (isLoading == 1) return

    const path = `/${i18n.language}/projects`

    if (pathname.startsWith(path) && !projectId && (projects.length > 0 || sharedProjects.length > 0)) {
      if (projects.length > 0) {
        navigate(getProjectPath(projects[projects.length - 1]))
      } else if (sharedProjects.length > 0) {
        navigate(getProjectPath(sharedProjects[sharedProjects.length - 1]))
      }
    } else if (pathname.startsWith(path) && projectId) {
      const projectExists = projects.findIndex((project) => project.id == projectId) != -1
      const sharedProjectExists = sharedProjects.findIndex((project) => project.id == projectId) != -1

      if (!projectExists && !sharedProjectExists) {
        if (projects.length > 0) {
          navigate(getProjectPath(projects[projects.length - 1]))
        } else if (sharedProjects.length > 0) {
          navigate(getProjectPath(sharedProjects[sharedProjects.length - 1]))
        } else {
          navigate(`/${i18n.language}/projects`)
        }
      }
    }
  }, [getProjectPath, i18n.language, isLoading, navigate, pathname, projectId, projects, sharedProjects])

  const selectedOption: Option | undefined = project && {
    id: project.id,
    label: project.name,
    data: project,
    Component: () => (
      <SelectedItemContainer>
        <SelectedIconContainer>
          <CaseIcon />
        </SelectedIconContainer>

        <SelectedItemLabel>{project.name}</SelectedItemLabel>
      </SelectedItemContainer>
    ),
  }

  const options: Option[] = [...projects, ...sharedProjects].map((project) => ({
    id: project.id,
    label: project.name,
    data: project,
  }))

  return (
    <Container>
      <Select
        disabledVerticalCorrection
        required
        classNames={{
          container: 'select-container',
          textInputContainer: 'text-input-container',
          expandContainer: 'expand-container',
        }}
        placeholder={t('modules_main_components_SelectProject_label_select_project')}
        selected={selectedOption}
        options={options}
        onClick={(option) => {
          if (option) {
            navigate(getProjectPath(option.data as Project))
          }
        }}
      />
    </Container>
  )
}
